import React, {
  type MutableRefObject,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import LoadingSpinner from '@fff-web/fff-ui-shared/LoadingSpinner';
import { getParamFromUrl } from '@fff-web/fff-utilities';
import { OptimizelyContext } from '@optimizely/react-sdk';
import { useRecurly } from '@recurly/react-recurly';
import classNames from 'classnames';
import {
  IBillingMethods,
  ICheckoutFormProps,
  ICustomizeOptions,
  IFormChangeEvent,
  PlanCode,
  RecurlyError,
  TokenPayload,
} from '../../../interfaces';
import {
  useApplePay,
  useCreditCardFormState,
  useCustomCheckoutPricing,
  usePayPal,
  useTrackField,
  useUniversalCouponInput,
} from '../../hooks';
import { usePricesContext } from '../../payments';
import { ShippingCountry } from '../../services/tax';
import {
  APPLE_PAY,
  CHECKOUT_STATE_FIELDS,
  CREDIT_CARD,
  PAYPAL,
  VENMO,
  paymentTypesDictionary,
} from '../../utils/constants';
import getPlanPriceByPlanCode from '../../utils/getPriceByPlanCode';
import { getProspectContextLogs } from '../../utils/getProspectContextLogs';
import getProspectFromState from '../../utils/getProspectFromState';
import { handleRegistration } from '../../utils/handleRegistration';
import { validateForm } from '../../utils/isValidCheckout';
import HCaptchaContainer from '../HCaptchaContainer';
import { OrderSummaryContainer } from '../OrderSummary';
import GtbOrderSummaryContainer from '../GtbOrderSummary';
import PaymentTabs from '../PaymentTabs';
import { Venmo } from '../Payments';
import CreditCardForm from '../Recurly/Forms/CreditCardForm';
import SubmitBtn from '../SubmitBtn';
import CreateAccount from '../CreateAccount';
import PaymentIcons from './PaymentIcons';
import ShippingLocation from '../ShippingLocation';
import { showGWP } from '../../utils/GWP';
import { checkoutReducer, createCheckoutState } from './reducer';
import {
  setAcceptedTerms,
  handleCaptchaError,
  handleCaptchaExpired,
  handleFieldChange,
  handleRecurlyTokenError,
  handleFormError,
  setBillingMethod,
  setCountry,
  setLoading,
  setTotal,
} from './reducer/actionCreators';
import { FormErrorBanner, TermsAndConditions, CountrySelect } from '../Shared';
import { AutoLoginError } from './helpers';

import styles from './CheckoutForm.module.css';

const applePayNotAvailableError = 'Apple Pay is not supported by this device or browser.';
const COUPON_CODE = 'coupon_code';
const TAX_SERVICE_OUTAGE = 'TAX_SERVICE_OUTAGE';

const ALT_PAY_WINDOW_KEYS = {
  applePay: 'applePay',
  payPal: 'payPal',
  venmo: 'venmo',
};

const PAYMENT_LABELS = {
  APPLE_PAY: 'Apple Pay',
  PAYPAL: 'PayPal',
  VENMO: 'Venmo',
  CREDIT_CARD: 'Credit Card',
};

const CheckoutForm: React.FunctionComponent<PropsWithChildren<ICheckoutFormProps>> = ({
  flags,
  fieldTracking,
  scrollToForm = () => null,
  showVenmoOption = false,
  customize,
  countries,
  handleExperimentTrackingOnSuccess = () => null,
  orderSummary,
  orderSummaryProps,
  CheckoutDisclaimer,
  disclaimerPriceIncrease,
  lineItems,
  selectedPlan,
  headerTitle,
  payContext,
  children,
  cartId,
  clickWrapConfig,
  setUniversalCouponState = () => null,
  legalDisclaimerExperiment,
}) => {
  const [isGwpChecked, setIsGwpChecked] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const altPaymentStateRef = useRef() as MutableRefObject<any>;
  const captchaRef: any = useRef({});

  const { optimizely } = useContext(OptimizelyContext);
  const recurly = useRecurly();
  const [checkoutState, dispatch] = useReducer(checkoutReducer, {}, createCheckoutState);
  const {
    email,
    password,
    shipCountry,
    shipState,
    shipZip,
    total,
    billingMethod,
    promoCode,
    loading,
    shipZipError,
    acceptedTerms,
    recurlyError,
    formError,
  } = checkoutState;
  const { prices } = usePricesContext();
  const payFieldsState = useCreditCardFormState(false);
  const { fields: fieldsState } = payFieldsState;
  useTrackField(fieldsState, billingMethod, fieldTracking);
  const productData = orderSummaryProps?.productData;

  const universalCouponInputState = useUniversalCouponInput({
    initialCouponCode: getParamFromUrl(COUPON_CODE),
    plan: selectedPlan,
    postalCode: shipZip,
    state: shipState,
    country: shipCountry,
  });

  const memoizedCouponState = useMemo(
    () => universalCouponInputState,
    [
      universalCouponInputState.couponData,
      universalCouponInputState.error,
      universalCouponInputState.promoCodeType,
      universalCouponInputState.loading,
      universalCouponInputState.coupon,
    ],
  );

  const {
    couponData,
    error: couponError,
    handleCallCouponApi,
    loading: couponLoading,
    coupon,
    handleOnChange: handleOnChangeCoupon,
    promoCodeType,
  } = memoizedCouponState;

  const {
    total: checkoutTotal,
    taxes,
    shipping,
    plan,
    discount,
    priceInGBP,
    previewErrors,
    isLoading: previewLoading,
  } = useCustomCheckoutPricing({
    planCode: selectedPlan as PlanCode,
    coupon: promoCodeType === 'coupon' ? couponData?.code : undefined,
    giftCard: promoCodeType === 'giftcard' ? couponData?.code : undefined,
    cartId,
    lineItemSkus: productData?.sku ? [productData?.sku] : [],
    subtotal: '',
    shipping: {
      country: shipCountry as ShippingCountry,
      state: shipState,
      zipCode: shipZip,
    },
  });

  useEffect(() => {
    setUniversalCouponState({ ...memoizedCouponState, discount });
  }, [memoizedCouponState, discount]);

  useEffect(() => {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const emailFromParam = params.get('email');
    if (emailFromParam) {
      handleFieldChange(dispatch, { name: 'email', value: emailFromParam });
    }
  }, []);

  const isCreditCardPayment = billingMethod === CREDIT_CARD;
  const isUSCountry = shipCountry === 'US';
  const hasRefill = productData?.hasRefill;
  const hasIntro = productData?.hasIntro;
  const isPublicShop = orderSummary === 'publicShop';
  const isFormValid = validateForm(checkoutState, billingMethod);

  const prospect = getProspectFromState(checkoutState, {
    firstName: payFieldsState?.fields?.first_name.value as string,
    lastName: payFieldsState?.fields?.last_name.value as string,
  });
  const address = {
    shipCountry: shipCountry as ShippingCountry,
    shipState,
    shipZipCode: shipZip,
  };

  // We have to check if coupontype exist for avoid ts error when accessing couponData.couponType, This is a problem when trying to access couponType on a variable that could be either type (ICoupon | IGiftCard).
  const showGWPCheckBox =
    couponData && 'couponType' in couponData && showGWP(couponData.couponType);

  const isSubmitButtonDisabled =
    !isFormValid || previewLoading || (!isGwpChecked && !!showGWPCheckBox);

  const clickWrapConfiguration = useMemo(
    () => ({
      ...clickWrapConfig,
      signerId: email,
      isFormValid,
    }),
    [clickWrapConfig, email, isFormValid],
  );

  useEffect(() => {
    if (isFormValid) datadogRum.addAction('GTB Order the Box CTA enabled', { billingMethod });
  }, [isFormValid]);

  const checkTermsAndShowErrorToast = () => {
    handleFormError(dispatch, {
      msg: 'Checkbox Required. Please select the required checkbox to proceed.',
    });
  };

  const handleFormStateOnChange = useCallback(
    (event: IFormChangeEvent | { target: { name: string; value: any } }) => {
      const { name, value } = event.target as HTMLSelectElement | HTMLInputElement;
      if (name === 'shipCountry') {
        setCountry(dispatch, { value });
      } else {
        handleFieldChange(dispatch, { name, value });
      }
    },
    [],
  );

  const handleBillingMethod = (method: IBillingMethods) => {
    setBillingMethod(dispatch, method);
    datadogLogs.logger.info(
      `Selected ${method} as payment method`,
      getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
    );
    datadogRum.setGlobalContextProperty('billingMethod', method);
    datadogLogs.setGlobalContextProperty('billingMethod', method);
  };

  // trigger proper payment flow according to billing method
  const handlePaymentFlow = () => {
    if (captchaRef?.current?.resetCaptcha) {
      captchaRef?.current?.resetCaptcha();
    }
    if (isPublicShop && hasIntro && !acceptedTerms) {
      return checkTermsAndShowErrorToast();
    }
    altPaymentStateRef.current = {
      prospect,
      billingMethod,
      recurlyError,
    };
    handleRecurlyTokenError(dispatch, '');
    handleFormError(dispatch, { msg: '' });
    setLoading(dispatch, true);
    switch (billingMethod) {
      case APPLE_PAY: {
        handleAltPayment(ALT_PAY_WINDOW_KEYS.applePay);
        break;
      }
      case PAYPAL: {
        handleAltPayment(ALT_PAY_WINDOW_KEYS.payPal);
        break;
      }
      case VENMO: {
        handleAltPayment(ALT_PAY_WINDOW_KEYS.venmo);
        break;
      }
      case CREDIT_CARD: {
        handleCCRecurlyToken();
        break;
      }
    }
  };

  const getHCaptchaToken = async (): Promise<string> => {
    const hCaptchaErrorMessage = `HCaptcha token error for ${altPaymentStateRef?.current?.billingMethod}`;
    datadogLogs.logger.info(
      `Requested HCaptcha token for ${altPaymentStateRef?.current?.billingMethod}`,
      getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
    );
    try {
      const token = await captchaRef.current?.execute({ async: true });
      if (token?.response) {
        datadogLogs.logger.info(
          `Received HCaptcha token for ${altPaymentStateRef?.current?.billingMethod}`,
          getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
        );
        return token.response;
      } else {
        const tokenNotAvailableError = new Error('HCaptcha token is not available');
        datadogLogs.logger.error(hCaptchaErrorMessage, {}, tokenNotAvailableError);
        throw tokenNotAvailableError;
      }
    } catch (err: any) {
      datadogLogs.logger.error(hCaptchaErrorMessage, {}, err);
      throw err;
    }
  };

  const handleCCRecurlyToken = async () => {
    datadogLogs.logger.info(
      `${CREDIT_CARD} flow started`,
      getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
    );
    try {
      const hCaptchaToken = await getHCaptchaToken();
      if (!formRef.current) {
        throw new Error('Form ref is not available');
      }

      recurly.token(
        formRef.current,
        async (err: RecurlyError | null, recurlyToken: TokenPayload): Promise<any> => {
          try {
            if (err) {
              const errorPayload =
                err.code === 'validation' ? 'Please fill out the missing fields.' : err.message;
              datadogLogs.logger.error(`Error generating Recurly ${CREDIT_CARD} token`, {}, err);
              handleFormError(dispatch, { msg: errorPayload });
              scrollToForm();
              throw err;
            }
            if (recurlyToken?.id) {
              datadogLogs.logger.info(
                `Received Recurly ${CREDIT_CARD} token`,
                getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
              );
              handleRegistration(
                recurlyToken?.id,
                prospect,
                customize as ICustomizeOptions,
                paymentTypesDictionary[billingMethod],
                payContext,
                countries,
                hCaptchaToken,
                handleOnSubmitError,
                total,
                handleExperimentTrackingOnSuccess,
                selectedPlan,
                cartId,
                productData,
                lineItems,
                recurlyError,
                optimizely,
              );
            } else {
              throw new Error('Recurly token is missing');
            }
          } catch (err: any) {
            handleRecurlyTokenError(dispatch, err);
            datadogLogs.logger.error(`Error handling ${CREDIT_CARD} payment`, {}, err);
          }
        },
      );
    } catch (err: any) {
      handleFormError(dispatch, err);
      handleRecurlyTokenError(dispatch, err);
      datadogLogs.logger.error(`Error handling ${CREDIT_CARD} payment`, {}, err);
      scrollToForm();
    }
  };

  const handleAltPayment = (altPaymentKey: string) => {
    const altPaymentObj = window[altPaymentKey as 'applePay' | 'payPal' | 'venmo'];
    if (altPaymentObj) {
      if (altPaymentKey === ALT_PAY_WINDOW_KEYS.applePay) {
        altPaymentObj.begin();
      } else altPaymentObj.start();
      datadogLogs.logger.info(
        `${billingMethod} flow started`,
        getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
      );
    } else {
      handleFormError(dispatch, {
        msg: `${
          PAYMENT_LABELS[billingMethod as IBillingMethods]
        } is not supported by this device or browser.`,
      });
      datadogLogs.logger.error(
        `User started ${billingMethod} flow but window object was not available`,
        getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
      );
    }
  };

  const onAltPaymentCancel = () => {
    datadogLogs.logger.info(
      `${altPaymentStateRef?.current?.billingMethod} flow canceled`,
      getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
    );
    setLoading(dispatch, false);
  };

  const onAltPaymentError = (error: any) => {
    datadogLogs.logger.error(
      `Error generating ${altPaymentStateRef?.current?.billingMethod} Recurly token`,
      {},
      error,
    );
    if (error?.message !== applePayNotAvailableError) {
      handleFormError(dispatch, { msg: error?.message });
    }
    handleRecurlyTokenError(dispatch, error);
  };

  const altPaySuccessCallback = async (recurlyToken: string) => {
    datadogLogs.logger.info(
      `Success generating ${altPaymentStateRef?.current?.billingMethod} Recurly token`,
    );
    try {
      const hCaptchaToken = await getHCaptchaToken();
      handleRegistration(
        recurlyToken,
        altPaymentStateRef?.current?.prospect,
        customize as ICustomizeOptions,
        paymentTypesDictionary[altPaymentStateRef?.current?.billingMethod as IBillingMethods],
        payContext,
        countries,
        hCaptchaToken as string,
        handleOnSubmitError,
        altPaymentStateRef?.current?.prospect?.total,
        handleExperimentTrackingOnSuccess,
        selectedPlan,
        cartId,
        productData,
        lineItems,
        altPaymentStateRef?.current?.recurlyError,
        optimizely,
      );
    } catch (err: any) {
      handleFormError(dispatch, err);
      datadogLogs.logger.error(
        `Error handling ${altPaymentStateRef?.current?.billingMethod} payment`,
        {},
        err,
      );
    }
  };

  const handleOnSubmitError = (error: string | AutoLoginError) => {
    if (typeof error === 'string') {
      handleFormError(dispatch, { msg: error });
    } else {
      handleFormError(dispatch, error);
    }
    scrollToForm();
  };

  useEffect(() => {
    datadogLogs.logger.info(
      'CheckoutForm rendered',
      getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
    );
  }, []);

  // Main purpose of this useEffect is to fire logs if quiz is present
  useEffect(() => {
    if (window.location.hash === 'start_amped_quiz') {
      datadogLogs.logger.info(
        'Quiz started',
        getProspectContextLogs({ ...prospect, planCode: selectedPlan }),
      );
    }
  }, [window.location.hash]);

  // TO DO: double check if this is needed
  useEffect(() => {
    const planTotal = getPlanPriceByPlanCode(prices, selectedPlan);
    setTotal(dispatch, planTotal);
  }, [prices, selectedPlan]);

  // need the total for the purchase event upon successful registration
  useEffect(() => {
    if (checkoutTotal) {
      setTotal(dispatch, checkoutTotal);
    }
  }, [checkoutTotal]);

  // setting tax preview error
  useEffect(() => {
    if (previewErrors && previewErrors.some((error) => error.type === TAX_SERVICE_OUTAGE)) {
      handleFieldChange(dispatch, {
        name: CHECKOUT_STATE_FIELDS.shipZipError,
        value: TAX_SERVICE_OUTAGE,
      });
    } else {
      handleFieldChange(dispatch, { name: CHECKOUT_STATE_FIELDS.shipZipError, value: null });
    }
  }, [previewErrors]);

  useEffect(() => {
    if (couponError) {
      handleFieldChange(dispatch, { name: CHECKOUT_STATE_FIELDS.promoCode, value: '' });
      return;
    }
    handleFieldChange(dispatch, { name: CHECKOUT_STATE_FIELDS.promoCode, value: couponData?.code });
  }, [couponData, couponError]);

  const { showApplePayOption } = useApplePay({
    recurly,
    checkoutTotal: total,
    onApplePayVerify: altPaySuccessCallback,
    onApplePayError: onAltPaymentError,
    onApplePayCancel: onAltPaymentCancel,
    flags,
  });

  usePayPal({
    recurly,
    onPaypalVerify: altPaySuccessCallback,
    onPaypalError: onAltPaymentError,
    onPaypalCancel: onAltPaymentCancel,
  });

  return (
    <>
      {loading && <LoadingSpinner />}
      {headerTitle && <h1 className={styles.coTitle}>{headerTitle}</h1>}
      <FormErrorBanner error={formError} email={email} />
      <section className={styles.coBody}>
        <section className={styles.coForm}>
          {children}
          <CreateAccount
            email={email}
            password={password}
            handleOnChange={handleFormStateOnChange}
          />
          <ShippingLocation
            handleFormStateOnChange={handleFormStateOnChange}
            countries={countries}
            shipCountry={shipCountry}
            shipState={shipState}
            shipZip={shipZip}
            shipZipError={shipZipError}
          />
          <section className={styles.pfContainer}>
            <PaymentIcons />
            <PaymentTabs
              billingMethod={billingMethod}
              setBillingMethod={handleBillingMethod}
              showVenmoOption={isUSCountry && showVenmoOption}
              showApplePayOption={showApplePayOption}
            />
            <form
              ref={formRef}
              className={classNames(styles.paymentForm, {
                [styles.paymentFormContainerHidden]: !isCreditCardPayment,
              })}
              data-testid="credit-card-form"
            >
              <CreditCardForm
                location="GTB"
                name={fieldsState.first_name}
                lastName={fieldsState.last_name}
                number={fieldsState.number}
                month={fieldsState.month}
                year={fieldsState.year}
                cvv={fieldsState.cvv}
                postalCode={fieldsState.postal_code}
                billingCountry={shipCountry}
              />
              <div className={styles.pfRow} hidden>
                <CountrySelect fieldState={fieldsState.country} billingCountry={shipCountry} />
              </div>
            </form>
          </section>
        </section>
        <section className={styles.coSummary}>
          {orderSummary === 'gtb' && (
            <GtbOrderSummaryContainer
              prospect={prospect}
              customize={customize}
              CheckoutDisclaimer={CheckoutDisclaimer}
              disclaimerPriceIncrease={disclaimerPriceIncrease}
              checkoutTotal={checkoutTotal}
              coupon={coupon}
              couponData={couponData}
              priceInGBP={priceInGBP}
              plan={plan}
              shipping={shipping}
              handleCallCouponApi={handleCallCouponApi}
              couponLoading={couponLoading}
              handleOnChangeCoupon={handleOnChangeCoupon}
              discount={discount}
              taxes={taxes}
              couponError={couponError}
              showGWPCheckBox={showGWPCheckBox}
              isGwpChecked={isGwpChecked}
              handleGWPCheckBoxChange={() => setIsGwpChecked(!isGwpChecked)}
              planCode={selectedPlan}
              legalDisclaimerExperiment={legalDisclaimerExperiment}
            />
          )}
          {isPublicShop && orderSummaryProps && (
            <OrderSummaryContainer
              {...{
                address,
                planCode: selectedPlan,
                promoCode,
                ...orderSummaryProps,
                cartId,
                acceptedTerms,
                setAcceptedTerms: (checked: boolean) => setAcceptedTerms(dispatch, checked),
                taxes,
                previewLoading,
                priceInGBP,
                shipping,
              }}
            />
          )}
          <TermsAndConditions
            clickWrapConfig={clickWrapConfiguration}
            billingMethod={billingMethod}
            isFormValid={isFormValid}
            hasRefill={hasRefill}
            isPublicShop={isPublicShop}
            hasIntroProducts={hasIntro}
            variation={legalDisclaimerExperiment}
          />
          <SubmitBtn
            isDisabled={isSubmitButtonDisabled}
            billingMethod={billingMethod}
            handleSubmit={handlePaymentFlow}
            payContext={payContext}
            prospect={prospect}
            planCode={selectedPlan}
          />
        </section>
      </section>
      {showVenmoOption && (
        <Venmo
          recurly={recurly as any}
          checkoutTotal={total}
          onVenmoVerify={altPaySuccessCallback}
          onVenmoError={onAltPaymentError}
          onVenmoCancel={onAltPaymentCancel}
        />
      )}
      <HCaptchaContainer
        ref={captchaRef}
        onExpire={() => {
          handleCaptchaExpired(dispatch);
        }}
        onError={(err) => {
          handleCaptchaError(dispatch, err);
        }}
      />
    </>
  );
};

export default CheckoutForm;
